import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
} from '@mui/material';
import { DarkModeOutlined, LightModeOutlined, Menu } from '@mui/icons-material';
import logoColor from '../../assets/HT_Horizontal_Color.svg';
import logoOnDark from '../../assets/HT_Horizontal_Color_OnDark.svg';
import { navItems } from '../../content/site';
import { useColorMode } from '../../colorMode';

const drawerWidth = 260;

export interface HeaderNavProps {
  window?: () => Window;
}

/** Sticky header with the primary navigation. */
const HeaderNav = (props: HeaderNavProps) => {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const { mode, toggleColorMode } = useColorMode();
  const themeLabel = mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  const logo = mode === 'dark' ? logoOnDark : logoColor;

  const handleDrawerToggle = () => {
    setMobileOpen((open) => !open);
  };

  const isActive = (to: string) => location.pathname === to;

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ py: 2 }}>
        <Box component="img" src={logo} alt="" sx={{ height: 36, width: 'auto', display: 'block' }} />
      </Box>
      <Divider />
      <List>
        {navItems.map(({ label, to }) => (
          <ListItem key={to} disablePadding>
            <ListItemButton component={Link} to={to} selected={isActive(to)}>
              <ListItemText primary={label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <AppBar
      component="header"
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: mode === 'dark' ? 2 : 1,
        borderColor: mode === 'dark' ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar sx={{ width: 'min(1120px, 100%)', mx: 'auto', minHeight: { xs: 64, sm: 76 }, px: { xs: 1.5, sm: 2 } }}>
        <IconButton
          color="inherit"
          aria-label="Open menu"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 1, display: { sm: 'none' } }}
        >
          <Menu />
        </IconButton>

        <Button component={Link} to="/" sx={{ p: 0.5, mr: 'auto' }} aria-label="Hochbegabt Technology, home">
          <Box component="img" src={logo} alt="Hochbegabt Technology" sx={{ height: 40, width: 'auto', display: 'block' }} />
        </Button>

        <Box component="nav" aria-label="Primary" sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
          {navItems.map(({ label, to }) => (
            <Button
              key={to}
              component={Link}
              to={to}
              aria-current={isActive(to) ? 'page' : undefined}
              sx={{
                color: isActive(to) ? 'primary.main' : 'text.primary',
                fontWeight: isActive(to) ? 700 : 600,
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
        <IconButton color="inherit" aria-label={themeLabel} onClick={toggleColorMode} sx={{ ml: 0.5 }}>
          {mode === 'light' ? <DarkModeOutlined /> : <LightModeOutlined />}
        </IconButton>
      </Toolbar>

      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default HeaderNav;
